import { Button } from '@/components/ui/button.tsx';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AnalysisResult, Plan } from "@ozon-ai/analysis/contracts";
import { ACTION_LABELS, STATUS_LABELS } from "@ozon-ai/analysis/contracts";
import { api } from '../api/client';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useTelegram } from '../context/TelegramContext';
import { ContentCard } from '../ui/ContentCard';
import { ErrorText, HintText, PageShell, SuccessText } from '../ui/PageShell';
import { StatusBadge } from '../ui/StatusBadge';

export function PlansPage() {
  const { webApp } = useTelegram();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await api.get<Plan[]>('/plans');
      setPlans(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить планы');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function triggerAnalysis() {
    setRunning(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.post<AnalysisResult>('/analyzer/trigger');
      const messages = formatAnalysisFeedback(result);
      if (messages.errors.length > 0) {
        webApp?.HapticFeedback?.notificationOccurred('error');
        setError(messages.errors.join(' · '));
      } else {
        webApp?.HapticFeedback?.notificationOccurred('success');
      }
      if (messages.notice) {
        setNotice(messages.notice);
      }
      await load();
    } catch (err) {
      webApp?.HapticFeedback?.notificationOccurred('error');
      setError(err instanceof Error ? err.message : 'Ошибка анализа');
    } finally {
      setRunning(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <PageShell
      title="Планы"
      action={
        <Button type="button" size="sm" disabled={running} onClick={() => void triggerAnalysis()}>
          {running ? 'Анализ…' : 'Запустить анализ'}
        </Button>
      }
    >
      {error && <ErrorText>{error}</ErrorText>}
      {notice && <SuccessText>{notice}</SuccessText>}

      {plans.length === 0 && (
        <ContentCard className="items-center text-center">
          <p className="text-sm">Пока нет планов.</p>
          <HintText>
            Запустите анализ вручную или дождитесь cron (по умолчанию раз в 3 дня).
          </HintText>
        </ContentCard>
      )}

      {plans
        .slice()
        .reverse()
        .map((plan) => (
          <ContentCard
            key={plan.id}
            title={plan.name}
            headerAction={
              <StatusBadge status={plan.status}>
                {STATUS_LABELS[plan.status] || plan.status}
              </StatusBadge>
            }
          >
            <HintText>{plan.description}</HintText>
            <HintText>
              {new Date(plan.createdAt).toLocaleString('ru-RU')} · {plan.actions.length} действий
            </HintText>
            <ul className="list-none space-y-1 pl-3">
              {plan.actions.slice(0, 4).map((action) => (
                <li key={action.id} className="text-sm text-muted-foreground">
                  {ACTION_LABELS[action.type] || action.type} —{' '}
                  {action.params.sku || action.params.offerId}
                </li>
              ))}
            </ul>
            <Button
              type="button"
              variant="ghost"
              className="mt-3 w-full"
              onClick={() => navigate('/actions')}
            >
              К аппруву действий
            </Button>
          </ContentCard>
        ))}
    </PageShell>
  );
}

function formatAnalysisFeedback(result: AnalysisResult): { errors: string[]; notice: string | null } {
  const errors = result.errors || [];
  const warnings = result.warnings || [];
  const parts: string[] = [];

  if (result.offersAnalyzed > 0 || result.actionsCount > 0) {
    parts.push(
      `Проанализировано ${result.offersAnalyzed} офферов, действий: ${result.actionsCount}`
    );
    if (result.planId) {
      parts.push(`план ${result.planId}`);
    }
  }

  if (warnings.length > 0) {
    parts.push(warnings.join(' · '));
  }

  return {
    errors,
    notice: parts.length > 0 ? parts.join('. ') : errors.length === 0 ? 'Анализ завершён' : null,
  };
}
